import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw } from "lucide-react";

/** Section wrapper: title, subtitle, optional right-hand actions. */
export const SectionCard = ({ title, subtitle, actions, children, className, testId, bodyClass }) => (
  <section className={cn("card-surface p-4 md:p-5", className)} data-testid={testId}>
    {(title || actions) && (
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="min-w-0">
          {title && <h3 className="font-display text-base md:text-lg leading-tight">{title}</h3>}
          {subtitle && <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{subtitle}</p>}
        </div>
        {actions && <div className="flex items-center gap-2 shrink-0 max-w-full flex-wrap">{actions}</div>}
      </div>
    )}
    <div className={bodyClass}>{children}</div>
  </section>
);

export const PageHeader = ({ eyebrow, title, subtitle, actions, testId }) => (
  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6 md:mb-8" data-testid={testId}>
    <div>
      {eyebrow && <div className="eyebrow mb-1.5">{eyebrow}</div>}
      <h1 className="font-display text-2xl md:text-3xl tracking-tight leading-tight">{title}</h1>
      {subtitle && <p className="text-sm text-muted-foreground mt-1.5 max-w-3xl leading-relaxed">{subtitle}</p>}
    </div>
    {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
  </div>
);

export const EmptyState = ({ icon: Icon, title, body, action, testId, className }) => (
  <div className={cn("card-surface p-8 md:p-10 flex flex-col items-start gap-3", className)} data-testid={testId || "empty-state"}>
    {Icon && (
      <span className="h-10 w-10 rounded-lg border border-border/70 bg-secondary/60 flex items-center justify-center text-gold"><Icon size={18} /></span>
    )}
    <div>
      <div className="font-display text-lg">{title}</div>
      {body && <p className="text-sm text-muted-foreground mt-1 max-w-xl leading-relaxed">{body}</p>}
    </div>
    {action}
  </div>
);

export const ErrorState = ({ message, onRetry, testId }) => (
  <div className="rounded-xl border border-danger/30 bg-danger/10 p-4 flex items-start gap-3" data-testid={testId || "error-alert"} role="alert">
    <AlertTriangle size={16} className="text-danger mt-0.5 shrink-0" />
    <div className="flex-1 text-sm">
      <div className="font-medium">Could not load this section</div>
      <div className="text-muted-foreground text-xs mt-0.5">{message}</div>
    </div>
    {onRetry && (
      <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={onRetry} data-testid="error-retry-button"><RefreshCw size={13} /> Retry</Button>
    )}
  </div>
);

export const ChartSkeleton = ({ height = 300 }) => <Skeleton className="w-full rounded-lg" style={{ height }} />;

export const Stat = ({ label, value, sub, className, testId }) => (
  <div className={cn("rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5", className)} data-testid={testId}>
    <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
    <div className="font-display text-lg tabular mt-0.5">{value}</div>
    {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
  </div>
);
